"use client";

import { getBackendUrl } from "@/lib/backend-url";

const TOKEN_KEY   = "mont2_admin_access_token";   // same keys as admin panel
const REFRESH_KEY = "mont2_admin_refresh_token";
const USER_KEY    = "mont2_admin_user";

export interface StudentUser {
  id: string;
  name: string;
  email: string;
  phone?: string | null;
  studentId?: string | null;
  roles: string[];
  permissions: string[];
}

export interface AuthTokens {
  accessToken?: string;
  token?: string;
  refreshToken: string;
  user: StudentUser;
}

export async function studentLogin(email: string, password: string): Promise<StudentUser> {
  studentLogout();
  const res = await fetch(`${getBackendUrl()}/api/v1/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const body = await res.json();
  if (!res.ok) throw new Error(body.message || "Login failed");
  const data: AuthTokens = body.data;
  const token = data.accessToken || data.token || "";
  // Use same keys as admin panel so the redirect works seamlessly
  localStorage.setItem(TOKEN_KEY,   token);
  localStorage.setItem(REFRESH_KEY, data.refreshToken || "");
  localStorage.setItem(USER_KEY,    JSON.stringify(data.user));
  return data.user;
}

/** 10–15 digits, optional leading country code. Mirrors backend validation. */
export const PHONE_PATTERN = /^\+?\d{10,15}$/;

export function normalizePhone(phone: string): string {
  return phone.replace(/[\s\-().]/g, "");
}

export async function studentRegister(
  name: string,
  email: string,
  password: string,
  phone: string,
): Promise<StudentUser> {
  const res = await fetch(`${getBackendUrl()}/api/v1/auth/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, email, password, phone: normalizePhone(phone) }),
  });
  const body = await res.json();
  if (!res.ok) throw new Error(body.message || "Registration failed");
  return studentLogin(email, password);
}

export function getStudentUser(): StudentUser | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as StudentUser) : null;
  } catch { return null; }
}

export function getStudentToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(TOKEN_KEY);
}

export function getStudentRefreshToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(REFRESH_KEY);
}

export function isStudent(user: StudentUser | null): boolean {
  if (!user) return false;
  const roles = (user.roles ?? []).map(r => r.toLowerCase());
  return roles.includes("student");
}

export function studentLogout(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
}

export async function fetchStudentDashboard(): Promise<Record<string, unknown> | null> {
  const token = getStudentToken();
  if (!token) return null;
  try {
    const res = await fetch(`${getBackendUrl()}/api/v1/dashboard/student`, {
      headers: { Authorization: `Bearer ${token}` },
      cache: "no-store",
    });
    if (!res.ok) return null;
    const body = await res.json();
    return body.data ?? null;
  } catch { return null; }
}

export async function fetchStudentPayments(): Promise<any[]> {
  const token = getStudentToken();
  if (!token) return [];
  try {
    const res = await fetch(`${getBackendUrl()}/api/v1/payments?page=1&limit=50`, {
      headers: { Authorization: `Bearer ${token}` },
      cache: "no-store",
    });
    if (!res.ok) return [];
    const body = await res.json();
    return body.data?.items ?? [];
  } catch { return []; }
}

// --- Progress types ---

export interface VideoProgress {
  videoId: string;
  title: string;
  sequence: number;
  duration: string;
  moduleName: string;
  batchId: string | null;
  batchName: string | null;
  completed: boolean;
  progressPercent: number;
  watchedSeconds: number;
  lastWatchedAt: string | null;
}

export interface ModuleProgress {
  moduleName: string;
  totalVideos: number;
  completedVideos: number;
  progressPercent: number;
}

export interface BatchProgress {
  batchId: string;
  batchName: string;
  totalVideos: number;
  completedVideos: number;
  progressPercent: number;
}

export interface CourseProgress {
  courseId: string;
  courseName: string;
  courseSlug: string;
  totalVideos: number;
  completedVideos: number;
  remainingVideos: number;
  overallPercentage: number;
  lastWatchedAt: string | null;
  modules: ModuleProgress[];
  batches: BatchProgress[];
  videos?: VideoProgress[];
}

export interface ProgressDashboard {
  userId: string;
  summary: {
    totalVideos: number;
    completedVideos: number;
    remainingVideos: number;
    overallPercentage: number;
    lastWatchedAt: string | null;
  };
  courses: CourseProgress[];
}

/** Fetch full progress dashboard (all courses with module + batch breakdowns) */
export async function fetchProgressDashboard(): Promise<ProgressDashboard | null> {
  const token = getStudentToken();
  if (!token) return null;
  try {
    const res = await fetch(`${getBackendUrl()}/api/v1/progress/dashboard`, {
      headers: { Authorization: `Bearer ${token}` },
      cache: "no-store",
    });
    if (!res.ok) return null;
    const body = await res.json();
    return (body.data ?? null) as ProgressDashboard | null;
  } catch { return null; }
}

/** Fetch detailed progress for a single course (includes per-video data with module/batch) */
export async function fetchCourseProgress(courseId: string): Promise<CourseProgress | null> {
  const token = getStudentToken();
  if (!token) return null;
  try {
    const res = await fetch(`${getBackendUrl()}/api/v1/progress/courses/${courseId}`, {
      headers: { Authorization: `Bearer ${token}` },
      cache: "no-store",
    });
    if (!res.ok) return null;
    const body = await res.json();
    return (body.data ?? null) as CourseProgress | null;
  } catch { return null; }
}

/** Mark a video as completed (called by button click or onEnded event) */
export async function markVideoCompleted(videoId: string): Promise<boolean> {
  const token = getStudentToken();
  if (!token) return false;
  try {
    const res = await fetch(`${getBackendUrl()}/api/v1/progress/videos/${videoId}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ completed: true, watchedSeconds: 9999 }),
    });
    return res.ok;
  } catch { return false; }
}

/**
 * Send a partial watch-progress update (called periodically while video plays).
 * Uses PATCH so it doesn't force-complete — the backend auto-completes once
 * watchedSeconds / durationSeconds >= the completion threshold.
 */
export async function updateVideoWatchProgress(
  videoId: string,
  watchedSeconds: number,
  durationSeconds: number,
): Promise<boolean> {
  const token = getStudentToken();
  if (!token) return false;
  try {
    const res = await fetch(`${getBackendUrl()}/api/v1/progress/videos/${videoId}`, {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ watchedSeconds, durationSeconds }),
    });
    return res.ok;
  } catch { return false; }
}
